import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Sun, Zap, Shield, Users, Wrench, Award } from "lucide-react";
import bgImage from "../images/solar-hero-bg.png";

const Home = () => {
  const services = [
    {
      icon: <Sun className="w-12 h-12 text-[#FFC107]" />,
      title: "Residential Solar Rooftop Systems",
      description:
        "Custom solar solutions for homes to reduce electricity bills and carbon footprint",
    },
    {
      icon: <Zap className="w-12 h-12 text-[#FFC107]" />,
      title: "Commercial & Industrial Solar",
      description:
        "Large-scale solar installations for businesses and industries",
    },
    {
      icon: <Shield className="w-12 h-12 text-[#FFC107]" />,
      title: "Solar Street Lights",
      description:
        "Eco-friendly lighting solutions for streets and public spaces",
    },
    {
      icon: <Wrench className="w-12 h-12 text-[#FFC107]" />,
      title: "Maintenance & AMC Services",
      description:
        "Comprehensive maintenance and support for your solar systems",
    },
  ];

  const testimonials = [
    {
      text: "Yours Energy Solutions transformed our home with a seamless solar installation. Our electricity bills have dropped by 70%!",
      author: "Rajesh Gogoi",
      location: "Dibrugarh",
    },
    {
      text: "Professional team, quality products, and excellent after-sales service. Highly recommended for commercial projects.",
      author: "Mithu Hazarika",
      location: "Sivasagar",
    },
    {
      text: "From consultation to installation, everything was handled perfectly. We are now energy independent!",
      author: "Lohit Dutta",
      location: "Jorhat",
    },
  ];

  const whyChooseUs = [
    {
      icon: <Award className="w-8 h-8 text-[#4CAF50]" />,
      text: "Over 200+ successful installations",
    },
    {
      icon: <Users className="w-8 h-8 text-[#4CAF50]" />,
      text: "Expert team of certified engineers",
    },
    {
      icon: <Shield className="w-8 h-8 text-[#4CAF50]" />,
      text: "Premium quality solar products",
    },
    {
      icon: <Wrench className="w-8 h-8 text-[#4CAF50]" />,
      text: "Comprehensive maintenance support",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* ================= Hero Section ================= */}
      <section
        className="relative h-[90vh] flex items-center justify-center overflow-hidden text-white mt-0 md:mt-14"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between">
          {/* Left side text */}
          <div className="text-left max-w-2xl space-y-5 md:space-y-10">
            <h1 className="text-3xl md:text-5xl font-bold leading-tight text-white drop-shadow-lg animate-fade-in">
              Professional <br className="hidden md:block" />
              Solar Panel Installation Service
            </h1>

            <p className="text-lg md:text-2xl text-gray-200 font-light">
              Cut Your Electricity Bills & Go Green with Trusted Solar Experts
            </p>

            <ul className="text-base md:text-lg space-y-1 text-gray-100">
              <li>✔ Top-Quality Solar Panels</li>
              <li>✔ Trusted by Customers Across Northeast India</li>
              <li>✔ Installation & Hassle-Free Service</li>
            </ul>

            <div className="pt-1">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Right spacing */}
          <div className="hidden md:block w-[45%]"></div>
        </div>

        {/* Leaflet-style bottom contact bar */}
        <div className="absolute bottom-0 inset-x-0 bg-[#013220]/90 py-3 md:py-4 px-6 md:px-12 flex flex-col md:flex-row items-center justify-between text-sm md:text-base font-medium">
          {/* Phone */}
          <a
            href="tel:8822367580"
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <span className="text-lg md:text-xl">📞</span>
            <span>8822367580</span>
          </a>

          {/* Address */}
          <p className="text-gray-200 mt-2 md:mt-0">📍  Beltola Guwahati , Old Amulapatty Sivsagar, Assam</p>
        </div>
      </section>

      {/* ================= About Us Preview ================= */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold text-[#0D47A1] mb-6">
              About Yours Energy Solutions
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              We deliver reliable and efficient solar rooftop systems to power
              a greener future for homes, schools, and businesses across Northeast India.
              Our mission is to make sustainable energy accessible and
              affordable for everyone.
            </p>
            <Link to="/about">
              <Button
                variant="outline"
                className="border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white transition-all duration-300"
              >
                Read More →
              </Button>
            </Link>
          </div>
          <div className="rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1668097613572-40b7c11c8727?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="Solar Installation"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            />
          </div>
        </div>
      </section>

      {/* ================= Our Services ================= */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0D47A1] mb-4">
            Our Services
          </h2>
          <p className="text-gray-600 mb-12 text-lg">
            Comprehensive solar solutions for every need
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-none bg-white"
              >
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">{service.icon}</div>
                  <h3 className="font-bold text-lg mb-3 text-[#0D47A1]">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#0D47A1] hover:bg-[#1565C0] text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ================= Why Choose Us ================= */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0D47A1] mb-12">
            Why Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="text-center group hover:scale-105 transition-transform duration-300"
              >
                <div className="flex justify-center mb-4">{item.icon}</div>
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Testimonials ================= */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0D47A1] mb-12">
            What Our Customers Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="hover:shadow-xl transition-all duration-300 border-none bg-white"
              >
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-4 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-[#0D47A1]">
                      {testimonial.author}
                    </p>
                    <p className="text-sm text-gray-600">
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Contact CTA ================= */}
      <section className="py-20 px-6 bg-[#0D47A1] text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">Ready to Go Solar?</h2>
          <p className="text-xl mb-8 text-gray-100">
            Let's talk today and start your journey towards clean, sustainable
            energy
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFC107] hover:bg-[#FFD54F] text-black font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl">
              Contact Us Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
